import { forwardRef, type HTMLAttributes } from 'react'

type BadgeVariant = 'default' | 'success' | 'warning' | 'error' | 'accent' | 'info'
type BadgeSize = 'sm' | 'md'

interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant
  size?: BadgeSize
}

const variantClasses: Record<BadgeVariant, string> = {
  default: 'bg-surface-elevated border-[var(--border-subtle)] text-[var(--text-secondary)]',
  success: 'bg-success/10 border-success/30 text-success',
  warning: 'bg-warning/10 border-warning/30 text-warning',
  error: 'bg-error/10 border-error/30 text-error',
  accent: 'bg-accent/10 border-accent/30 text-accent',
  info: 'bg-info/10 border-info/30 text-info',
}

const sizeClasses: Record<BadgeSize, string> = {
  sm: 'px-1.5 py-0.5 text-[10px]',
  md: 'px-2.5 py-1 text-xs',
}

/**
 * Badge - Status indicator with semantic variants
 *
 * @example
 * <Badge variant="success">Approved</Badge>
 *
 * @example
 * <Badge variant="warning" size="sm">Pending</Badge>
 *
 * @example
 * <Badge variant="error">Failed</Badge>
 */
const Badge = forwardRef<HTMLSpanElement, BadgeProps>(
  ({ variant = 'default', size = 'md', className = '', children, ...props }, ref) => {
    return (
      <span
        ref={ref}
        className={`inline-flex items-center gap-1.5 font-mono uppercase tracking-wider border ${variantClasses[variant]} ${sizeClasses[size]} ${className}`}
        {...props}
      >
        {children}
      </span>
    )
  }
)

Badge.displayName = 'Badge'

export default Badge
